import {
  SidePanelDS,
  Assets,
  Spacer,
  LockOutlined,
  TextCapitalized,
  Switch,
  Avatar,
  RemoveRedEyeOutlined,
  Tag,
  ButtonDS,
} from "@qivia/ui";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CardsQrCodesModal from "../../modals/cards/cardsQrCodesModal";
import { QrCodeInfo, CardStatus } from "../cardsAPI";
import { VehicleType } from "@qivia/schemas-cards-vehicles";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";

type Informations = {
  name: string | null;
  lastName?: string;
  uuid: string | null;
  reference?: string | null;
  model?: string | null;
};

type VehicleInformation = Informations & {
  type: VehicleType;
};

export const InformationsCard = (props: {
  isCardLocked: boolean;
  firstPath: string;
  lockCard: (isCardLocked: boolean, cardUuid: string) => void;
  vehicle: VehicleInformation;
  rule: Informations;
  driver: Informations;
  cardUuid: string;
  qrCodes: QrCodeInfo[];
  canRedirectToRule?: boolean;
  getQrCodePresignedUrlAction: (qrCode: QrCodeInfo) => void;
  shipmentLink: string;
  cardStatus: CardStatus;
  topContent?: JSX.Element;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isQrCodeModalOpen, setIsQrCodeModalOpen] = useState(false);

  const qrCodeButtonText = useMemo(
    () =>
      props.qrCodes.length > 1
        ? t("cards.sidePanel.button.openQrCodes")
        : t("cards.sidePanel.button.openQrCode"),
    [props.qrCodes.length, t],
  );

  const isPinBlocked = useMemo(
    () =>
      props.cardStatus === "INACTIVE_PIN_ONLINE" ||
      props.cardStatus === "BLOCKED_PIN_OFFLINE",
    [props.cardStatus],
  );

  return (
    <SidePanelDS.Section>
      <StyledAssets>
        {props.isCardLocked ? (
          <Assets.LockedQiviaCard />
        ) : (
          <Assets.UnlockedQiviaCard />
        )}
      </StyledAssets>
      <Spacer y={1.5} />
      {props.topContent && (
        <>
          {props.topContent}
          <Spacer y={1} />
        </>
      )}
      {props.shipmentLink && (
        <StyledLinkedWord
          href={props.shipmentLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <TextCapitalized>{t("cards.sidePanel.shipmentLink")}</TextCapitalized>
        </StyledLinkedWord>
      )}
      <Spacer y={1.5} />
      {!isPinBlocked && (
        <>
          <SidePanelDS.ActionBlock>
            <SidePanelDS.SectionedActionStripe
              left={
                <StyledRowCenter>
                  <LockOutlined />
                  <Spacer x={0.25} />
                  <TextCapitalized>{t("cards.sidePanel.lock")}</TextCapitalized>
                </StyledRowCenter>
              }
              right={
                <Switch
                  onClick={() =>
                    props.lockCard(!props.isCardLocked, props.cardUuid)
                  }
                  checked={props.isCardLocked || false}
                  size={"S"}
                />
              }
            />
          </SidePanelDS.ActionBlock>
          <Spacer y={1} />
        </>
      )}
      <SidePanelDS.ActionBlock>
        <>
          {props.driver.name && props.driver.lastName && (
            <SidePanelDS.SectionedActionStripe
              left={
                <StyledRowCenter>
                  <TextCapitalized>
                    <Avatar
                      size={"S"}
                      color={"orange"}
                      firstName={props.driver.name[0]}
                      lastName={props.driver.lastName[0]}
                    />
                  </TextCapitalized>
                  <Spacer x={0.5} />
                  {props.driver.name + " " + props.driver.lastName}
                </StyledRowCenter>
              }
              right={
                <StyledLittleIcon>
                  <RemoveRedEyeOutlined
                    key="driver"
                    onClick={() =>
                      navigate(
                        `/${props.firstPath}/drivers/${props.driver.uuid}?fromNavigate=true`,
                      )
                    }
                  />
                  <Spacer x={1} />
                </StyledLittleIcon>
              }
            />
          )}
        </>
        <SidePanelDS.SectionedActionStripe
          left={
            <StyledRowCenter>
              {props.vehicle.name}
              {props.vehicle.reference && (
                <>
                  <Spacer x={1} />
                  <Tag
                    textColor={colors["colors/text/darkGrey"]}
                    text={props.vehicle.reference}
                    backgroundColor={
                      colors["colors/surfaces/background/background_level0"]
                    }
                    borderColor={colors["colors/text/darkGrey"]}
                  />
                </>
              )}
            </StyledRowCenter>
          }
          right={
            <StyledLittleIcon>
              <RemoveRedEyeOutlined
                key="vehicle"
                onClick={() =>
                  navigate(
                    `/${props.firstPath}/vehicles/${props.vehicle.uuid}?fromNavigate=true`,
                  )
                }
              />
              <Spacer x={1} />
            </StyledLittleIcon>
          }
        />
        <SidePanelDS.SectionedActionStripe
          left={
            <StyledRowCenter>
              <StyledBodyM>
                <TextCapitalized>
                  {t(`cards.sidePanel.informations.title.rule`)}
                </TextCapitalized>
              </StyledBodyM>
              <Spacer x={0.5} />
              {props.rule.name}
            </StyledRowCenter>
          }
          right={
            props.canRedirectToRule && (
              <StyledLittleIcon>
                <RemoveRedEyeOutlined
                  key="rule"
                  onClick={() =>
                    navigate(`/${props.firstPath}/rule/${props.rule.uuid}`)
                  }
                />
                <Spacer x={1} />
              </StyledLittleIcon>
            )
          }
        />
      </SidePanelDS.ActionBlock>
      <Spacer y={1} />
      <ButtonDS
        buttonType="secondary"
        leftIcon={<RemoveRedEyeOutlined />}
        text={qrCodeButtonText}
        format={"fill"}
        onClick={() => props.qrCodes.length > 0 && setIsQrCodeModalOpen(true)}
        disabled={props.qrCodes.length < 1}
      />
      <CardsQrCodesModal
        vehicle={props.vehicle}
        qrCodes={props.qrCodes}
        isQrCodeModalOpen={isQrCodeModalOpen}
        setIsQrCodeModalOpen={setIsQrCodeModalOpen}
        getQrCodePresignedUrlAction={props.getQrCodePresignedUrlAction}
      />
    </SidePanelDS.Section>
  );
};

const StyledBodyM = styled.div`
  ${typographies["Body/M"]}
`;

const StyledLinkedWord = styled.a`
  display: flex;
  color: ${colors["colors/system/informative/informative_normal"]};
  justify-content: center;
`;

const StyledRowCenter = styled.div`
  ${typographies["Body/S"]}
  display: flex;
  align-items: center;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;
const StyledLittleIcon = styled.div`
  display: flex;
  cursor: pointer;
  & svg {
    width: 1rem;
    height: 1rem;
  }
`;
const StyledAssets = styled.div`
  display: flex;
  justify-content: center;
  & svg {
    width: 13rem;
    height: 9.5rem;
  }
`;
